import { Button, Input } from '@bennows/boows-react';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { api, ApiFailure, call, fieldErrorCodes } from '../../lib/api';
import { AuthCard } from './AuthCard';

/** The set-password link from an operator invitation (REQ-140-002); sign-in and the authenticator follow. */
export function InvitationPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const accept = useMutation({
    mutationFn: () => call(() => api.POST('/v1/operator/invitations/{token}/acceptance', { params: { path: { token } }, body: { password } })),
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const differs = password !== repeat;
    setMismatch(differs);
    if (!differs) accept.mutate();
  };

  if (accept.isSuccess) {
    return (
      <AuthCard>
        <h1>{t('invitation.title')}</h1>
        <p className="ui-auth__notice" role="status">
          {t('invitation.done')}
        </p>
        <Link className="ui-auth__link" to="/sign-in">
          {t('invitation.toSignIn')}
        </Link>
      </AuthCard>
    );
  }

  const failure = accept.isPending ? null : accept.error;
  const slug = failure instanceof ApiFailure ? failure.slug : failure ? 'unexpected' : null;
  if (slug === 'invitation-invalid' || slug === 'not-found') {
    return (
      <AuthCard>
        <h1>{t('invitation.title')}</h1>
        <p className="ui-auth__message" role="alert">
          {t('invitation.invalid')}
        </p>
      </AuthCard>
    );
  }
  const rules = slug === 'validation-failed' ? fieldErrorCodes(failure instanceof ApiFailure ? failure.body : undefined, 'password') : [];
  const message = slug && slug !== 'validation-failed' ? (slug === 'rate-limited' ? 'common.rateLimited' : slug === 'network' ? 'common.errorNetwork' : 'common.errorUnexpected') : null;

  return (
    <AuthCard>
      <h1>{t('invitation.title')}</h1>
      <p className="ui-auth__lead">{t('invitation.lead')}</p>
      <form onSubmit={onSubmit} noValidate>
        <Input
          label={t('invitation.password')}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          required
          invalid={rules.length > 0}
          help={rules.length > 0 ? rules.map((code) => t(`invitation.rule.${code}`)).join(' ') : t('invitation.passwordHelp')}
        />
        <Input
          label={t('invitation.confirm')}
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={setRepeat}
          required
          invalid={mismatch}
          help={mismatch ? t('invitation.mismatch') : undefined}
        />
        {message ? (
          <p className="ui-auth__message" role="alert">
            {t(message)}
          </p>
        ) : null}
        <Button type="submit" loading={accept.isPending} block>
          {t('invitation.submit')}
        </Button>
      </form>
    </AuthCard>
  );
}
